// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import React, { ChangeEvent, useEffect, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FaUser, FaMapMarkerAlt, FaCalendarAlt, FaSlidersH, FaSuitcase } from 'react-icons/fa';

import { useAppSelector } from '../../../app/hooks';
import { selectVehicleTypeById } from '../../../app/vehicle-type/vehicle-typeSlice';
import { RootState } from '../../../app/store';
import { getDaysBetweenDates } from '../../../utils/getDaysBetweenDates';

interface BookingModalProps {
  vt: number;
  show: boolean;
  onHide: () => void;
  onSubmit: (values: BookingFormData) => void;
}

interface BookingFormData {
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  pickUpTown: string;
  pickUpArea: string;
  drivingToTown: string;
  drivingToCountry: string;
  dropOffTown: string;
  startDate: Date | null;
  endDate: Date | null;
  vehicleType: string | number;
  driveMode: string;
  numOfAdults: number;
  numOfChildren: number;
  selectedRate: string;
  accessories?: string[];
}

const ACCESSORIES = ['Jerry Cans', 'Satellite Phone', 'GPS', 'Cooler Box', 'Ground tent / Mattresses / Beddings', 'Camping Equipments'];

export const BookingModalRental: React.FC<BookingModalProps> = ({ vt, show, onHide, onSubmit }) => {
  let vehicleType = useAppSelector((state: RootState) => selectVehicleTypeById(state, vt));

  const [numOfDays, setNumOfDays] = useState(0);
  const [start, setStart] = useState<Date>(new Date());
  const [end, setEnd] = useState<Date>(new Date());
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) {
      setNumOfDays(0);
    }
  }, [show]);

  const validateStart = () => {
    const now = new Date();
    if (!start) {
      setError('Start date/time is required');
      return false;
    }
    if (start < now) {
      setError("Start date/time can't be in the past");
      return false;
    }
    if (start < new Date(now.getTime() + 2 * 60 * 60 * 1000)) {
      setError('Start date/time must be at least 2 hours ahead of the current time');
      return false;
    }
    setError('');
    return true;
  };
  const validateEnd = () => {
    if (end < start) {
      setError('End date/time must be after the start date/time');
      return false;
    }
    if (!end) {
      setError('End date/time is required');
      return false;
    }
    setError('');
    return true;
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    pickUpTown: Yup.string().required('Pickup Town is required'),
    drivingToTown: Yup.string().required('Driving To Town is required'),
    dropOffTown: Yup.string().required('Dropoff Town is required'),
    vehicleType: Yup.mixed().required('Vehicle Type is required'),
    numOfAdults: Yup.number().required('Number of Adults is required').min(1, 'Number of Adults must be at least 1'),
    numOfChildren: Yup.number().min(0, 'Number of Children cannot be less than 0'),
    driveMode: Yup.string().required('Please indicate if you need a driver or not'),
    selectedRate: Yup.string().required('Currency is required'),
  });

  useEffect(() => {
    setNumOfDays(getDaysBetweenDates(start?.toString(), end?.toString()) || 0);
    validateStart();
    validateEnd();
  }, [start, end]);

  return (
    <Modal show={show} onHide={() => { onHide(); setNumOfDays(0); }} dialogClassName='booking-modal' size='lg'>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Rent a Vehicle</Modal.Title>
          <div className='booking-subtitle'>Fill in your details to get an instant quote</div>
          {vehicleType && <span className='vehicle-badge'>{vehicleType.type}</span>}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Formik<BookingFormData>
          initialValues={{
            email: '',
            firstname: '',
            lastname: '',
            phone: '',
            pickUpTown: '',
            pickUpArea: '',
            drivingToTown: '',
            drivingToCountry: 'Zambia',
            dropOffTown: '',
            startDate: null,
            endDate: null,
            vehicleType: vehicleType?.id,
            driveMode: '',
            numOfAdults: 0,
            numOfChildren: 0,
            selectedRate: 'ZMW',
            accessories: [],
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit}>

              {/* Personal Details */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaUser /> Personal Details
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='firstname'>
                    <Form.Label>First Name</Form.Label>
                    <Field type='text' name='firstname' as={Form.Control} placeholder='John' required />
                  </Form.Group>
                  <Form.Group controlId='lastname'>
                    <Form.Label>Last Name</Form.Label>
                    <Field type='text' name='lastname' as={Form.Control} placeholder='Doe' required />
                  </Form.Group>
                  <Form.Group controlId='email'>
                    <Form.Label>Email Address</Form.Label>
                    <Field type='email' name='email' as={Form.Control} placeholder='john@example.com' required />
                    <ErrorMessage name='email' component='div' className='error-message' />
                  </Form.Group>
                  <Form.Group controlId='phone'>
                    <Form.Label>Phone</Form.Label>
                    <Field type='phone' name='phone' as={Form.Control} placeholder='+260 97...' required />
                  </Form.Group>
                </div>
              </div>

              {/* Trip Details */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaMapMarkerAlt /> Trip Details
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='pickUpTown'>
                    <Form.Label>Pickup Town</Form.Label>
                    <Field type='text' name='pickUpTown' as={Form.Control} placeholder='e.g. Lusaka' required />
                  </Form.Group>
                  <Form.Group controlId='pickUpArea'>
                    <Form.Label>Pickup Area</Form.Label>
                    <Field type='text' name='pickUpArea' as={Form.Control} placeholder='e.g. Kabulonga' required />
                  </Form.Group>
                  <Form.Group controlId='drivingToTown'>
                    <Form.Label>Driving To Town</Form.Label>
                    <Field type='text' name='drivingToTown' as={Form.Control} placeholder='e.g. Livingstone' required />
                  </Form.Group>
                  <Form.Group controlId='drivingToCountry'>
                    <Form.Label>Driving To Country</Form.Label>
                    <Field type='text' name='drivingToCountry' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='dropOffTown'>
                    <Form.Label>Drop-off Town</Form.Label>
                    <Field type='text' name='dropOffTown' as={Form.Control} placeholder='e.g. Lusaka' required />
                  </Form.Group>
                  <Form.Group controlId='vehicleType'>
                    <Form.Label>Vehicle Type</Form.Label>
                    <Field as='select' name='vehicleType' disabled className='form-control' required>
                      <option value={vehicleType?.id}>{vehicleType?.type}</option>
                    </Field>
                  </Form.Group>
                </div>
              </div>

              {/* Schedule */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaCalendarAlt /> Schedule
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='startDate'>
                    <Form.Label>Start Date & Pickup Time</Form.Label>
                    <Field
                      type='datetime-local'
                      name='startDate'
                      as={Form.Control}
                      required
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        formikProps.handleChange(e);
                        if (e.target.value) {
                          setStart(new Date(e.target.value));
                          formikProps.setFieldValue('startDate', e.target.value)
                        }
                        validateStart();
                      }}
                    />
                  </Form.Group>
                  <Form.Group controlId='endDate'>
                    <Form.Label>End Date & Drop-off Time</Form.Label>
                    <Field
                      type='datetime-local'
                      name='endDate'
                      as={Form.Control}
                      required
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        formikProps.handleChange(e);
                        if (e.target.value) {
                          setEnd(new Date(e.target.value));
                          formikProps.setFieldValue('endDate', e.target.value)
                        }
                        validateEnd();
                      }}
                    />
                  </Form.Group>
                </div>
                {error !== '' && <div className='error-message mt-2'>{error}</div>}
                <div className='mt-3'>
                  <div className={`hire-days-badge ${numOfDays < 2 ? 'invalid' : ''}`}>
                    {numOfDays} {numOfDays === 1 ? 'day' : 'days'}
                  </div>
                  {numOfDays < 2 && <span className='error-message ms-2'>Minimum 2 days required</span>}
                </div>
              </div>

              {/* Preferences */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaSlidersH /> Preferences
                </div>

                <div className='mb-3'>
                  <Form.Label>Do you need a driver?</Form.Label>
                  <div className='toggle-btn-group'>
                    <button
                      type='button'
                      className={`toggle-btn ${formikProps.values.driveMode === 'Chauffeur' ? 'active' : ''}`}
                      onClick={() => formikProps.setFieldValue('driveMode', 'Chauffeur')}
                    >
                      Yes, Chauffeur
                    </button>
                    <button
                      type='button'
                      className={`toggle-btn ${formikProps.values.driveMode === 'Self-drive' ? 'active' : ''}`}
                      onClick={() => formikProps.setFieldValue('driveMode', 'Self-drive')}
                    >
                      No, Self-drive
                    </button>
                  </div>
                  <ErrorMessage name='driveMode' component='div' className='error-message' />
                </div>

                <div className='mb-3'>
                  <Form.Label>Currency</Form.Label>
                  <div className='toggle-btn-group'>
                    <button
                      type='button'
                      className={`toggle-btn ${formikProps.values.selectedRate === 'USD' ? 'active' : ''}`}
                      onClick={() => formikProps.setFieldValue('selectedRate', 'USD')}
                    >
                      $ USD
                    </button>
                    <button
                      type='button'
                      className={`toggle-btn ${formikProps.values.selectedRate === 'ZMW' ? 'active' : ''}`}
                      onClick={() => formikProps.setFieldValue('selectedRate', 'ZMW')}
                    >
                      K ZMW
                    </button>
                  </div>
                  <ErrorMessage name='selectedRate' component='div' className='error-message' />
                </div>

                <div className='booking-fields-row'>
                  <Form.Group controlId='numOfAdults'>
                    <Form.Label>No. of Adults</Form.Label>
                    <Field type='number' name='numOfAdults' as={Form.Control} required min={1} />
                    <ErrorMessage name='numOfAdults' component='div' className='error-message' />
                  </Form.Group>
                  <Form.Group controlId='numOfChildren'>
                    <Form.Label>No. of Children</Form.Label>
                    <Field type='number' name='numOfChildren' as={Form.Control} min={0} />
                    <ErrorMessage name='numOfChildren' component='div' className='error-message' />
                  </Form.Group>
                </div>
              </div>

              {/* Accessories */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaSuitcase /> Optional Accessories
                </div>
                <div className='accessory-chips'>
                  {ACCESSORIES.map((acc) => {
                    const isSelected = formikProps.values.accessories?.includes(acc);
                    return (
                      <label
                        key={acc}
                        className={`accessory-chip ${isSelected ? 'selected' : ''}`}
                      >
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              formikProps.setFieldValue('accessories', [...(formikProps.values.accessories || []), acc]);
                            } else {
                              formikProps.setFieldValue('accessories', (formikProps.values.accessories || []).filter(a => a !== acc));
                            }
                          }}
                        />
                        {acc}
                      </label>
                    );
                  })}
                </div>
                <small className='text-muted d-block mt-2'>Select any extras you need for your trip.</small>
              </div>

              <button
                className='booking-cta'
                type='submit'
                disabled={!!(formikProps.isSubmitting || !formikProps.isValid) || numOfDays < 2 || error !== ''}
              >
                Get Quote
              </button>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
